import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import {
  deletePasskey,
  type Passkey,
  listPasskeys,
  registerPasskey,
} from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { useTu } from "@src/appV2/i18n/useTu";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { routes } from "@src/appV2/redesign/utils/routes";
import { ModalStatus, type UseModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { ResponsiveDialog } from "../../components/ResponsiveDialog";
import { Text } from "../../components/Text";

/**
 * URL-driven passkey management surface (DEVOP-5458). Mounted at
 * `/my-account/passkeys` so it's deep-linkable while rendering as the standard
 * ResponsiveDialog used elsewhere in My Account. Lists the user's passkeys and
 * lets them add or remove one.
 */
interface Props {
  modalState?: UseModalState;
}

export function ManagePasskeysDialog(props: Props) {
  const { modalState: providedModalState } = props;
  const { tu } = useTu("myAccount");
  const history = useHistory();
  const location = useLocation();

  const isRouteOpen = location.pathname.replace(/\/+$/, "") === routes.myAccountPasskeys;

  // Keep the My Account entry point addressable through the URL. Login prompts
  // can provide their own modal state because that route is unavailable to admins.
  const routeModalState: UseModalState = {
    modalStatus: isRouteOpen ? ModalStatus.OPEN : ModalStatus.CLOSED,
    setModalStatus: () => undefined,
    modalIsOpen: isRouteOpen,
    modalIsClosed: !isRouteOpen,
    openModal: () => history.push(routes.myAccountPasskeys),
    // Replace (not push) so the Back button doesn't land on the modal route and reopen the dialog.
    closeModal: () => history.replace(routes.myAccount),
  };
  const modalState = providedModalState ?? routeModalState;
  const isOpen = modalState.modalIsOpen;

  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | undefined>();

  const loadPasskeys = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      setPasskeys(await listPasskeys());
    } catch (error: unknown) {
      setLoadError(true);
      showErrorToast(mapCognitoError(error, "passkey").message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      void loadPasskeys();
    }
  }, [isOpen, loadPasskeys]);

  async function handleAdd() {
    setIsAdding(true);
    try {
      await registerPasskey();
      logEvent(APP_V2_APP_EVENTS.PASSKEY_REGISTERED);
      showSuccessToast(tu("Passkey added"));
      await loadPasskeys();
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "passkey");
      logError(APP_V2_APP_EVENTS.PASSKEY_REGISTRATION_FAILURE, {
        error,
        metadata: { errorCode: mapped.code },
      });
      showErrorToast(mapped.message);
    } finally {
      setIsAdding(false);
    }
  }

  async function handleRemove(credentialId: string) {
    setRemovingId(credentialId);
    try {
      await deletePasskey(credentialId);
      showSuccessToast(tu("Passkey removed"));
      await loadPasskeys();
    } catch (error: unknown) {
      showErrorToast(mapCognitoError(error, "passkey").message);
    } finally {
      setRemovingId(undefined);
    }
  }

  return (
    <ResponsiveDialog
      modalState={modalState}
      title={tu("Passkeys")}
      width={480}
      footerContent={
        <DialogFooter
          primaryAction={{
            label: tu("Add a passkey"),
            isLoading: isAdding,
            onClick: () => {
              void handleAdd();
            },
          }}
          secondaryAction={{
            label: tu("Done"),
            onClick: modalState.closeModal,
          }}
        />
      }
    >
      <Stack spacing={3}>
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {tu(
            "A passkey lets you sign in with your device's fingerprint, face, or screen lock — no password or email link needed. It's faster and more secure. After adding one, choose \"Sign in with a passkey\" on the login screen and confirm with your device."
          )}
        </Text>

        <Alert severity="warning">
          {tu(
            "Only add a passkey on a device that's yours. Don't add one on a shared or public computer — anyone who can unlock that device could sign in as you."
          )}
        </Alert>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", paddingY: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Text semibold variant="body2">
              {tu("Your passkeys")}
            </Text>
            {loadError ? (
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.error.main })}>
                {tu("We couldn't load your passkeys. Please try again.")}
              </Text>
            ) : passkeys.length === 0 ? (
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {tu("You haven't added any passkeys yet.")}
              </Text>
            ) : (
              passkeys.map((passkey) => (
                <Stack
                  key={passkey.credentialId}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={(theme) => ({
                    paddingY: theme.spacing(2),
                    "&:not(:last-child)": {
                      borderBottom: `1px solid ${String(theme.palette.divider)}`,
                    },
                  })}
                >
                  <Stack sx={{ minWidth: 0 }}>
                    <Text variant="body2" sx={{ overflowWrap: "anywhere" }}>
                      {passkey.friendlyName || tu("Passkey")}
                    </Text>
                    {passkey.createdAt && (
                      <Text
                        variant="body2"
                        sx={(theme) => ({ color: theme.palette.text.secondary })}
                      >
                        {tu("Added {{date}}", {
                          values: { date: passkey.createdAt.toLocaleString() },
                        })}
                      </Text>
                    )}
                  </Stack>
                  <Button
                    variant="outlined"
                    disabled={removingId === passkey.credentialId}
                    onClick={() => {
                      void handleRemove(passkey.credentialId);
                    }}
                  >
                    {removingId === passkey.credentialId ? tu("Removing...") : tu("Remove")}
                  </Button>
                </Stack>
              ))
            )}
          </Stack>
        )}
      </Stack>
    </ResponsiveDialog>
  );
}
